import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { CheckCircle, XCircle, Clock, Calendar, Eye } from "lucide-react";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { DataTable } from "../components/dataTable";
import { createAdminLeaveColumns, Leave } from "../lib/leavesTableColumns";

interface LeaveStats {
  pending: number;
  approved: number;
  rejected: number;
  totalDays: number;
}

export default function AdminLeaves() {
  const navigate = useNavigate();
  const location = useLocation();
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [stats, setStats] = useState<LeaveStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Dialog states
  const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "reject" | null>(
    null
  );
  const [rejectionReason, setRejectionReason] = useState("");
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState(null, "");

      setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
    }
  }, [location.state]);

  useEffect(() => {
    fetchLeaves();
    fetchStats();
  }, [statusFilter]);

  const fetchLeaves = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/leaves/all?status=${statusFilter}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const normalized = (response.data || []).map((leave: any) => ({
        ...leave,
        id: leave._id,
      }));

      setLeaves(normalized);
    } catch (error: any) {
      console.error("Error fetching leaves:", error);
      setError(error.response?.data?.message || "Failed to fetch leaves");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/leaves/stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleApproveClick = (leave: Leave) => {
    setSelectedLeave(leave);
    setActionType("approve");
    setIsActionDialogOpen(true);
  };

  const handleRejectClick = (leave: Leave) => {
    setSelectedLeave(leave);
    setActionType("reject");
    setRejectionReason("");
    setIsActionDialogOpen(true);
  };

  const handleViewClick = (leave: Leave) => {
    setSelectedLeave(leave);
    setIsViewDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedLeave || !actionType) return;

    try {
      setIsApproving(true);
      const token = localStorage.getItem("authToken");

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}api/leaves/${selectedLeave._id}/status`,
        {
          status: actionType === "approve" ? "Approved" : "Rejected",
          rejectionReason:
            actionType === "reject" ? rejectionReason : undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccessMessage(
        `Leave ${
          actionType === "approve" ? "approved" : "rejected"
        } successfully!`
      );
      setIsActionDialogOpen(false);
      setSelectedLeave(null);
      setActionType(null);
      setRejectionReason("");
      fetchLeaves();
      fetchStats();
    } catch (error: any) {
      console.error("Error updating leave status:", error);
      setError(
        error.response?.data?.message || "Failed to update leave status"
      );
    } finally {
      setIsApproving(false);
    }
  };

  const columns = createAdminLeaveColumns(
    handleApproveClick,
    handleRejectClick,
    handleViewClick
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Spinner variant="ellipsis" size={40} />
          <p className="text-sm text-muted-foreground">Loading leaves...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Leave Requests</h1>
          <p className="text-muted-foreground mt-1">
            Manage all employee leave applications
          </p>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-green-800 font-medium">{successMessage}</p>
          </div>
          <button
            onClick={() => setSuccessMessage("")}
            className="text-green-600 hover:text-green-800"
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">{error}</p>
          </div>
          <button
            onClick={() => setError("")}
            className="text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pending Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-600" />
                <span className="text-2xl font-bold">{stats.pending}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Approved
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-2xl font-bold">{stats.approved}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Rejected
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                <span className="text-2xl font-bold">{stats.rejected}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Days
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                <span className="text-2xl font-bold">{stats.totalDays}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      <div className="mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Leaves</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Approved">Approved</SelectItem>
            <SelectItem value="Rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Leaves Table */}

      <DataTable columns={columns} data={leaves} />

      {/* View Leave Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Leave Application Details</DialogTitle>
            <DialogDescription>
              Complete information about the leave request
            </DialogDescription>
          </DialogHeader>

          {selectedLeave && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Employee Name</Label>
                  <p className="font-medium mt-1">
                    {selectedLeave.employee?.fullName ||
                      selectedLeave.employeeName}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium mt-1">
                    {selectedLeave.employee?.email ||
                      selectedLeave.employeeEmail}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Leave Type</Label>
                  <p className="font-medium mt-1">{selectedLeave.leaveType}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Mode</Label>
                  <div className="mt-1">
                    <Badge
                      className={
                        selectedLeave.leaveMode === "comp-off" ||
                        selectedLeave.compOffId
                          ? "bg-purple-100 text-purple-800"
                          : "bg-blue-100 text-blue-800"
                      }
                    >
                      {selectedLeave.leaveMode === "comp-off" ||
                      selectedLeave.compOffId
                        ? "Comp-Off"
                        : "Regular"}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <div className="mt-1">
                    <Badge
                      className={
                        selectedLeave.isPaidLeave
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }
                    >
                      {selectedLeave.isPaidLeave ? "Paid" : "Unpaid"}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Start Date</Label>
                  <p className="font-medium mt-1">
                    {new Date(selectedLeave.startDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">End Date</Label>
                  <p className="font-medium mt-1">
                    {new Date(selectedLeave.endDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    Number of Days
                  </Label>
                  <p className="font-medium mt-1">
                    {selectedLeave.numberOfDays}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    <Badge
                      className={
                        selectedLeave.status === "Approved"
                          ? "bg-green-100 text-green-800"
                          : selectedLeave.status === "Rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }
                    >
                      {selectedLeave.status}
                    </Badge>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">Reason</Label>
                <p className="mt-1 p-3 bg-muted rounded-md">
                  {selectedLeave.reason}
                </p>
              </div>

              {(selectedLeave.leaveMode === "comp-off" ||
                selectedLeave.compOffId ||
                selectedLeave.compOff) && (
                <div className="grid grid-cols-2 gap-4 p-3 bg-purple-50 rounded-md border border-purple-100">
                  <div>
                    <Label className="text-muted-foreground">Comp-Off Earned</Label>
                    <p className="font-medium mt-1">
                      {selectedLeave.compOff?.workDate
                        ? new Date(
                            selectedLeave.compOff.workDate
                          ).toLocaleDateString()
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Credit Days</Label>
                    <p className="font-medium mt-1">
                      {selectedLeave.compOff?.creditDays ?? "—"}
                    </p>
                  </div>
                </div>
              )}

              {selectedLeave.rejectionReason && (
                <div>
                  <Label className="text-muted-foreground text-red-600">
                    Rejection Reason
                  </Label>
                  <p className="mt-1 p-3 bg-red-50 text-red-800 rounded-md border border-red-200">
                    {selectedLeave.rejectionReason}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsViewDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve/Reject Dialog */}
      <Dialog open={isActionDialogOpen} onOpenChange={setIsActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === "approve" ? "Approve Leave" : "Reject Leave"}
            </DialogTitle>
            <DialogDescription>
              {actionType === "approve"
                ? "Are you sure you want to approve this leave request?"
                : "Please provide a reason for rejecting this leave request."}
            </DialogDescription>
          </DialogHeader>

          {selectedLeave && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-md space-y-2">
                <p>
                  <span className="font-medium">Employee:</span>{" "}
                  {selectedLeave.employee?.fullName ||
                    selectedLeave.employeeName}
                </p>
                <p>
                  <span className="font-medium">Leave Type:</span>{" "}
                  {selectedLeave.leaveType}
                </p>
                <p>
                  <span className="font-medium">Duration:</span>{" "}
                  {new Date(selectedLeave.startDate).toLocaleDateString()} -{" "}
                  {new Date(selectedLeave.endDate).toLocaleDateString()} (
                  {selectedLeave.numberOfDays} days)
                </p>
              </div>

              {actionType === "reject" && (
                <div>
                  <Label htmlFor="rejectionReason">
                    Rejection Reason <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="rejectionReason"
                    placeholder="Enter reason for rejection..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="mt-2"
                    rows={4}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsActionDialogOpen(false);
                setRejectionReason("");
              }}
              disabled={isApproving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              disabled={
                isApproving ||
                (actionType === "reject" && !rejectionReason.trim())
              }
              className={
                actionType === "approve"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              }
            >
              {isApproving ? (
                <>
                  <Spinner variant="ellipsis" size={16} className="mr-2" />
                  Processing...
                </>
              ) : actionType === "approve" ? (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Approve
                </>
              ) : (
                <>
                  <XCircle className="mr-2 h-4 w-4" />
                  Reject
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
